<template>
  <div
    class="hero-banner critical-component-clear"
    :class="[
      `hero-banner--${ bannerContent.horizontalAlignment }`,
      `hero-banner--${ bannerContent.verticalAlignment }`,
      { 'hero-banner__new-layout': bannerContent.newLayout },
    ]"
  >
    <div class="hero-banner__images-wrapper">
      <a
        v-if="bannerContent.ctaLink"
        :href="bannerContent.ctaLink"
        class="hero-banner__block-link"
      >
        <div v-if="bannerContent.heroSource === 'image'">
          <div
            v-if="bannerContent.desktopImage && !isMobile"
            class="hero-banner__desktop-image"
          >
            <responsive-image
              :image="bannerContent.desktopImage"
              ratio="16:9"
            />
          </div>

          <div
            v-if="bannerContent.mobileImage && isMobile"
            class="hero-banner__mobile-image"
          >
            <responsive-image
              :image="bannerContent.mobileImage"
              ratio="4:5"
            />
          </div>
        </div>

        <div v-else>
          <video-element
            v-if="bannerContent.heroDesktopVideo"
            class="hero-banner__desktop-video"
            :poster="bannerContent.heroDesktopVideo.preview_image.src"
            :srcset="bannerContent.heroDesktopVideo.sources"
            :loop="bannerContent.heroVideoLoop"
            :mute="bannerContent.heroVideoMute"
            :autoplay="bannerContent.heroVideoAutoplay"
            :controls="bannerContent.heroVideoControls"
          />

          <video-element
            v-if="bannerContent.heroMobileVideo"
            class="hero-banner__mobile-video"
            :poster="bannerContent.heroMobileVideo.preview_image.src"
            :srcset="bannerContent.heroMobileVideo.sources"
            :loop="bannerContent.heroVideoLoop"
            :mute="bannerContent.heroVideoMute"
            :autoplay="bannerContent.heroVideoAutoplay"
            :controls="bannerContent.heroVideoControls"
          />
        </div>
      </a>

      <div v-else>
        <div v-if="bannerContent.heroSource === 'image'">
          <div
            v-if="bannerContent.desktopImage && !isMobile"
            class="hero-banner__desktop-image"
          >
            <responsive-image
              :image="bannerContent.desktopImage"
              ratio="16:9"
            />
          </div>

          <div
            v-if="bannerContent.mobileImage && isMobile"
            class="hero-banner__mobile-image"
          >
            <responsive-image
              :image="bannerContent.mobileImage"
              ratio="4:5"
            />
          </div>
        </div>

        <div v-else>
          <video-element
            class="hero-banner__desktop-video"
            :poster="bannerContent.heroDesktopVideo.preview_image.src"
            :srcset="bannerContent.heroDesktopVideo.sources"
            :loop="bannerContent.heroVideoLoop"
            :mute="bannerContent.heroVideoMute"
            :autoplay="bannerContent.heroVideoAutoplay"
            :controls="bannerContent.heroVideoControls"
          />

          <video-element
            class="hero-banner__mobile-video"
            :poster="bannerContent.heroMobileVideo.preview_image.src"
            :srcset="bannerContent.heroMobileVideo.sources"
            :loop="bannerContent.heroVideoLoop"
            :mute="bannerContent.heroVideoMute"
            :autoplay="bannerContent.heroVideoAutoplay"
            :controls="bannerContent.heroVideoControls"
          />
        </div>
      </div>
    </div>

    <div class="hero-banner__text">
      <div class="hero-banner__text-wrap">
        <h1
          class="hero-banner__header"
          :style="{ color: bannerContent.color }"
          v-text="bannerContent.header"
        />

        <p
          v-if="bannerContent.subtitle"
          class="hero-banner__subtitle"
          :style="{ color: bannerContent.color }"
          v-html="bannerContent.subtitle"
        />

        <div
          v-if="showCtaWrapper"
          class="hero-banner__cta-wrapper"
          :style="`--cta-color: ${bannerContent.ctaColor};`"
        >
          <btn
            v-if="bannerContent.ctaLabel"
            class="in-grid-banner__button"
            modifiers="text"
            :show-icon="false"
            :label="bannerContent.ctaLabel"
            :style="{color: bannerContent.ctaColor}"
            @click="handleCtaClick(bannerContent.ctaLink)"
          />

          <btn
            v-if="bannerContent.cta2Label"
            class="in-grid-banner__button"
            modifiers="text"
            :show-icon="false"
            :label="bannerContent.cta2Label"
            :style="{color: bannerContent.ctaColor}"
            @click="handleCtaClick(bannerContent.cta2Link)"
          />
        </div>
      </div>

      <div
        v-if="showCtaCarousel"
        class="hero-banner__cta-carousel"
      >
        <a
          v-for="cta, index of bannerContent.ctaCarouselButtons"
          :key="index"
          :href="cta.url"
        >
          <btn
            class="hero-banner__cta-carousel-button"
            :class="[urlIsActive(cta.url) ? disabledClass : null]"
            type="button"
            modifiers="light center"
            :show-icon="false"
            :label="cta.title"
          />
        </a>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * The hero banner section.
 * - Static section.
 *
 * @param {Object} bannerContent - Meta information from collection object
 */

import { mapState } from 'vuex'
import classes from '~/config/classes'
import Btn from '~global/btn/btn'
import VideoElement from '~global/video-element/video-element'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'HeroBanner',

  components: {
    Btn,
    VideoElement,
    ResponsiveImage,
  },

  props: {
    bannerContent: {
      type: Object,
      default: () => ({
        newLayout: null,
        heroSource: null,
        heroDesktopVideo: null,
        heroDesktopPoster: null,
        heroMobileVideo: null,
        heroMobilePoster: null,
        heroVideoMute: null,
        heroVideoLoop: null,
        heroVideoAutoplay: null,
        heroVideoControls: null,
        desktopImage: null,
        mobileImage: null,
        header: null,
        subtitle: null,
        color: null,
        ctaColor: null,
        horizontalAlignment: null,
        verticalAlignment: null,
        ctaLabel: null,
        ctaLink: null,
        ctaShopTheLook: false,
        ctaShopTheLookCollection: null,
        cta2Label: null,
        cta2Link: null,
        cta2ShopTheLook: false,
        cta2ShopTheLookCollection: null,
        enableCtaCarousel: false,
        ctaCarouselButtons: null,
      }),
    },
  },

  emits: 'shop-the-look',

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute disabled class
     * @returns {String}
     */
    disabledClass() {
      return classes.disabled
    },

    /**
     * Compute if CTA wrapper should be shown.
     * @returns {Boolean}
     */
    showCtaWrapper() {
      const { ctaLabel, ctaLink } = this.bannerContent

      if (ctaLabel && ctaLink) {
        return true
      }

      return false
    },

    /**
     * Compute if CTA carousel should be shown.
     * @returns {Boolean}
     */
    showCtaCarousel() {
      const { enableCtaCarousel, ctaCarouselButtons } = this.bannerContent

      if (enableCtaCarousel && ctaCarouselButtons.length) {
        return true
      }

      return false
    },
  },

  methods: {

    /**
     * Get image alt.
     * @param {String | Object} img - Image from section setting
     */
    getImageAlt(img) {
      if (typeof img === 'object' && img.alt) {
        return img.alt
      }

      return ''
    },

    /**
     * Get image src.
     * @param {String | Object} img - Image from section setting
     */
    getImageSrc(img) {
      if (typeof img === 'object' && img.src) {
        return img.src
      }

      return img
    },

    /**
     * Determine if link href matches current pages pathname.
     * @param {String} path
     * @returns {Boolean}
     */
    urlIsActive(path) {
      return window.location.pathname === path
    },

    /**
     * Handle CTA click - open Shop the Look or proceed to link url.
     * @param {String} link - Link for button clicked.
     */
    handleCtaClick(link) {
      const {
        ctaShopTheLook,
        ctaShopTheLookCollection,
        cta2ShopTheLook,
        cta2ShopTheLookCollection,
      } = this.bannerContent

      let enable = ctaShopTheLook
      let collection = ctaShopTheLookCollection

      if (link === this.bannerContent.cta2Link) {
        enable = cta2ShopTheLook
        collection = cta2ShopTheLookCollection
      }

      if (enable && collection !== null) {
        window.cnvs.EventBus.emit('shop-the-look', { collection })

        return
      }

      window.location = link
    },
  },
}
</script>

<style lang="scss">
@import './hero-banner';
</style>
